/**
 * Consequence copy — PER-RECORD-LENS-1 (s158).
 *
 * Source of truth: `docs/archive/working-notes/2026-08-06/s158-consequence-copy-deck.md`, approved
 * by Reade. Sentences are TRANSCRIBED from the deck — never paraphrased, never re-authored here.
 *
 * Voice rule (D-147.22): every line describes what the DOCUMENT does with the value. Never an
 * assessment of the person, never "the deceased" on a planner surface.
 *
 * Structure (deck Decision 1 → **Option B**): a per-artifact DEFAULT sentence, plus shared VARIANTS
 * that override it where the field's ROLE differs. Option A (one sentence per artifact, full stop)
 * was rejected on its own failure case: under a *Date of birth* gap, "The bank notification names
 * the account it applies to" misexplains the field.
 *
 * 🔴 No sentence string may be written inline in a component. This module is the only place they live.
 */

/** The three shared variants (deck Decision 1). V-REF is not a string: "the artifact's own
 *  names-the-account/policy line" IS the default, so a V-REF field simply takes the default. */
export const CONSEQUENCE_VARIANTS = {
  /** V-ID — birth date / names on letters. */
  V_ID: "It confirms who the letter concerns.",
  /** V-ADDR — the recipient institution / person / mailing address. */
  V_ADDR: "The letter is addressed with it.",
} as const;
export type ConsequenceVariant = keyof typeof CONSEQUENCE_VARIANTS;

/** Per-artifact default sentence, keyed by the artifact id the lens emits (form id / letter type). */
export const ARTIFACT_SENTENCES: Readonly<Record<string, string>> = {
  // ── Government forms & applications (deck: "Forms genuinely ask; the honest sentence is that
  //    they ask. Deliberately plain.") ──────────────────────────────────────────────────────────
  "ISP-1201": "Service Canada matches the record with it before cancelling benefits.",
  "CRA-NOD": "The CRA death notification identifies the taxpayer with it.",
  cra_notification: "The CRA death notification identifies the taxpayer with it.", // deck #3 = "(same as #2)"
  "ISP-1200": "The CPP death benefit application asks for it.",
  "ISP-1300": "The survivor's pension application asks for it.",
  "74A": "The probate application asks for it.",
  "74.1A": "The small-estate application asks for it.",
  "74D": "The affidavit of execution records when the will was signed.",
  EIR: "The estate information return asks for it.",
  TX19: "The clearance certificate request asks for it.",

  // ── Notification & claim letters ──────────────────────────────────────────────────────────────
  employer_active_notification: "The employer notification identifies the employment it concerns.",
  employer_retired_notification: "The employer notification identifies the employment it concerns.", // deck #5 = "(same as #4)"
  bank_notification: "The bank notification names the account it applies to.",
  credit_card_notification: "The card cancellation letter names the account it applies to.",
  equifax_notification: "Equifax matches the file with it before flagging it.",
  transunion_notification: "TransUnion matches the file with it before flagging it.",
  life_insurance_claim: "The insurance claim identifies the policy with it.",
  dod_valuation_request: "The valuation request quotes it to the institution.",

  // ── Approved s158 alongside the deck, after the letter-title rider surfaced them ───────────────
  // These six never appeared in the deck's copy tables because at deck time their tasks pointed at
  // untitled duplicate keys. Three were repointed at canonical letters by the rider; three are the
  // fallback-titled keys that could not be repointed without guessing. Drafted to the deck's own
  // pattern and approved as drafted.
  //
  // 🔴 All six declare `[]` in LETTER_FIELD_NEEDS, so their only planner field is `date_of_birth`
  // from LETTER_COMMON_NEEDS — which carries the V-ID override below. These defaults are therefore
  // currently UNREACHABLE; they exist so the artifact is never uncovered if one of these letters
  // later declares a per-record field of its own (as `bank_notification` does).
  mortgage_notification: "The mortgage notification names the loan it applies to.",
  beneficiary_notification: "The beneficiary notification identifies the estate it concerns.",
  charity_beneficiary_notification: "The charity notification identifies the bequest it concerns.",
  "tenancy-termination-notice": "The termination notice names the tenancy it ends.",
  "employer-benefits": "The benefits claim identifies the employment it concerns.",
  "beneficiary-update": "The progress update identifies the estate it concerns.",

  // s171 (R-A): the veterans_affairs letter template already existed but no task pointed at it,
  // so the lens could never emit it and it needed no sentence. Binding it to
  // `check-veterans-benefits` made it emittable — which is exactly what this guard is for.
  veterans_affairs: "The Veterans Affairs notification identifies the service record it concerns.",
};

/**
 * Per-field overrides, keyed `artifactId` → `fieldKey` → variant or literal sentence.
 * Transcribed from the deck's Variants column; a field absent here takes the artifact default.
 */
export const CONSEQUENCE_FIELD_OVERRIDES: Readonly<
  Record<string, Readonly<Record<string, ConsequenceVariant | string>>>
> = {
  employer_active_notification: {
    // deck #4: an explicit literal, not V-ADDR — the employer letter is mailed to the address.
    employer_address: "The employer notification is mailed to it.",
    hr_contact_name: "V_ADDR",
    date_of_birth: "V_ID",
  },
  employer_retired_notification: {
    employer_address: "The employer notification is mailed to it.",
    hr_contact_name: "V_ADDR",
    date_of_birth: "V_ID",
  },
  bank_notification: { bank: "V_ADDR", date_of_birth: "V_ID" },
  credit_card_notification: { creditor: "V_ADDR", date_of_birth: "V_ID" },
  life_insurance_claim: { provider: "V_ADDR", date_of_birth: "V_ID" },
  dod_valuation_request: { institution: "V_ADDR" },
  // The repointed + fallback-titled letters: `date_of_birth` takes V-ID exactly as it does on every
  // other letter in the deck (#4/#6/#7/#12). The variant is approved copy; only the artifact
  // DEFAULTS above are pending.
  mortgage_notification: { date_of_birth: "V_ID" },
  beneficiary_notification: { date_of_birth: "V_ID" },
  charity_beneficiary_notification: { date_of_birth: "V_ID" },
  "tenancy-termination-notice": { date_of_birth: "V_ID" },
  "employer-benefits": { date_of_birth: "V_ID" },
  "beneficiary-update": { date_of_birth: "V_ID" },
};

/** The `<field_key>` half of a planner ref (`<section-slug>:<field_key>`). */
function fieldKeyOf(ref: string): string {
  const i = ref.indexOf(":");
  return i < 0 ? ref : ref.slice(i + 1);
}

/**
 * The consequence sentence for one (artifact, planner-field) pair.
 *
 * Resolution: a per-field override (variant key or literal) → the artifact's default sentence →
 * `null`. `null` means the artifact has no declared copy, which the coverage guard treats as a
 * defect; a caller must never invent a line at render time.
 */
export function consequenceSentence(artifactId: string, ref: string): string | null {
  const override = CONSEQUENCE_FIELD_OVERRIDES[artifactId]?.[fieldKeyOf(ref)];
  if (override) {
    return override in CONSEQUENCE_VARIANTS
      ? CONSEQUENCE_VARIANTS[override as ConsequenceVariant]
      : override;
  }
  return ARTIFACT_SENTENCES[artifactId] ?? null;
}

/** Does this artifact have a declared sentence? Used by the coverage guard. */
export function hasArtifactSentence(artifactId: string): boolean {
  return artifactId in ARTIFACT_SENTENCES;
}

// ── EXECUTOR-GAP-PANEL-1 (s199) — the SECOND voice ──────────────────────────────────────────────
//
// Ruled s199 (SPEC "Two voices, on purpose"): a **critical**-tier gap may carry a HAND-WRITTEN line,
// because that is where words change what someone does. Every other tier keeps the generated
// sentence above, which writes itself for all 103 tasks and never goes stale — roughly a dozen lines
// get written by a person, not hundreds.
//
// 🔴 It is an OVERRIDE MAP with the generated line as fallback, never a second copy system and never
// a blank when an override is missing. `gapSentence` below is the ONE resolver; a component reads it
// and no component ever holds a sentence string (the module rule at the top of this file).
//
// 🔴 RULED AND WRITTEN BY READE AT s200 (CRITICAL-CONSEQUENCE-COPY-1). The s199 SPEC placed the exact
// wording out of scope and the map shipped `{}` deliberately until he wrote it; he has. These sixteen
// sentences are TRANSCRIBED, never authored and never re-authored — the same provenance rule the deck
// sentences above carry. A future session may add a key or correct a typo Reade rules on; it may not
// rewrite a line to read better.
//
// Both constraints they were drafted against, verified by the guard in
// `server/__tests__/critical-gap-sentences.test.ts`:
//   • Voice (D-147.22) — each line says what the DOCUMENT does with the value. No assessment of the
//     person, and never "the deceased" on a planner surface.
//   • 🔴 Jurisdiction seam — no region-specific office, form or designation, and no literal "executor"
//     (that term resolves through EXECUTOR_TERM). All sixteen were written to avoid the word entirely,
//     so no substitution is needed here.
//
// Still an OVERRIDE MAP: a ref absent from it falls back to the generated sentence, never to a blank.
export const CRITICAL_GAP_SENTENCES: Readonly<Record<string, string>> = {
  "about-you:date_of_birth": "Every form and letter confirms your identity with it.",
  "about-you:first_name": "Your legal first name appears on everything filed for your estate.",
  "about-you:middle_name": "Forms ask for your full legal name, exactly as your records hold it.",
  "about-you:family_name": "It is how institutions find your records.",
  "about-you:name_at_birth": "Older records are held under it, and institutions search there first.",
  "about-you:marital_status": "It decides which survivor benefits and transfers can be claimed.",
  "about-you:home_address": "Letters are addressed with it, and it sets where the estate is administered.",
  "about-you:last_occupation": "Registering the death asks for it before anything else can proceed.",

  "children-dependents-overview:first_name": "Every claim made on their behalf names them.",
  "children-dependents-overview:surname": "A child's surname may differ from yours, and claims are filed under theirs.",
  "children-dependents-overview:dob": "Benefit claims for a child are assessed on their age.",
  "children-dependents-overview:relationship": "It establishes what the child is entitled to.",

  "beneficiaries:beneficiary_surname": "It is the name transfers are made out in.",
  "beneficiaries:beneficiary_address": "Notices and payments are sent to it.",
  "beneficiaries:beneficiary_email": "It is the fastest way to reach them without waiting on post.",
  "beneficiaries:beneficiary_relationship": "It determines what each person receives and how it is taxed.",
};

/**
 * The line a gap renders. `critical` consults the hand-written override first; every tier falls back
 * to the engine's generated sentence, which is already on the gap.
 *
 * 🔴 Never returns a blank for a gap that had a generated line — a missing override is a fallback,
 * not an empty row.
 */
export function gapSentence(gap: { ref: string; tier: string; consequence: string }): string {
  if (gap.tier === "critical") {
    const written = CRITICAL_GAP_SENTENCES[gap.ref];
    if (written) return written;
  }
  return gap.consequence;
}
